import React, { useState, useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/react-hooks'
import {
  CreateVoucherCostCategoryInput,
  CREATE_VOUCHER_COST_CATEGORY,
  GET_UPLOAD_LINK,
  DELETE_RECEIPT,
  GET_DOWNLOAD_LINK,
} from './gql'
import { VoucherReceipt } from '../../types'
import {
  TableCell,
  TableRow,
  Button,
  styled,
  List,
  ListItem,
  Typography,
  IconButton,
  Tooltip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import FilePreviewButton from '../Common/FilePreviewButton'


type ReceiptsProps = {
  voucherId: string
  voucherCostCategory: any
  onCategoryChange: any
  voucherReceipts: VoucherReceipt[]
  paidByAmin: any
  readonly?: boolean;
};
const Receipts: React.FunctionComponent<ReceiptsProps> = ({
  voucherId,
  voucherCostCategory,
  onCategoryChange,
  voucherReceipts,
  paidByAmin,
  readonly = false,
}) => {
  const [createVoucherCostCategory] = useMutation(CREATE_VOUCHER_COST_CATEGORY)
  const [deleteReceipt] = useMutation(DELETE_RECEIPT)
  const [getFileDownloadLink] = useLazyQuery(GET_DOWNLOAD_LINK, {
    onCompleted: (data) => {
      if (data?.signedDownloadLink?.signedLink) {
        window.open(data.signedDownloadLink.signedLink, '_blank')
      }
    },
  })
  const [vCostCategoryId, setVCostCategoryId] = useState(voucherCostCategory.id)
  const [receipts, setReceipts] = useState<Array<VoucherReceipt>>([])

  useEffect(() => {
    setReceipts(voucherReceipts)
  }, [voucherReceipts])

  const [file, setFile] = useState<File | null>(null)
  const [getFileUploadLink] = useMutation(GET_UPLOAD_LINK, {
    onCompleted: async (uploadLinkData) => {
      await uploadFile(file, uploadLinkData.getUploadLink.signedLink)
      if (file) {
        const newReceipts = [
          {
            id: uploadLinkData.getUploadLink.receiptId,
            voucherId,
            path: file.name,
          },
          ...receipts,
        ];

        if (/^-?\d+$/.test(voucherCostCategory.id)) {
          onCategoryChange(voucherCostCategory.id, {
            ...voucherCostCategory,
            id: vCostCategoryId,
            receipts: newReceipts,
          })
        } else {
          setReceipts(newReceipts)
        }
      }
    },
  })

  async function uploadFile(fileObject, presignedUrl) {
    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': fileObject.type,
      },
      body: fileObject,
    }
    const response = await fetch(presignedUrl, requestOptions)
    return response
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0])

      let newVoucherCostCategoryId = voucherCostCategory.id
      if (/^-?\d+$/.test(voucherCostCategory.id)) {
        const newVoucherCostCategoryInput: CreateVoucherCostCategoryInput = {
          voucher_id: voucherId,
          cost_category_id: voucherCostCategory.cost_category.id,
          cost_share: voucherCostCategory.cost_share,
        }
        const newVoucherCostCategoryResponse = await createVoucherCostCategory({
          variables: { input: newVoucherCostCategoryInput },
        })
        const newVoucherCostCategory =
          newVoucherCostCategoryResponse.data.createVoucherCostCategory
        newVoucherCostCategoryId = newVoucherCostCategory.id
      }

      if (/^-?\d+$/.test(voucherCostCategory.id)) {
        setVCostCategoryId(newVoucherCostCategoryId)
      }

      getFileUploadLink({
        variables: {
          input: {
            fileName: e.target.files[0].name,
            voucherCostCategoryId: newVoucherCostCategoryId,
            voucherId,
          },
        },
      })
    }
  }

  const handleDeleteReceipt = async (receipt: VoucherReceipt) => {
    if (
      window.confirm(
        `Are you sure you want to delete this receipt ${receipt.path}?`
      )
    ) {
      await deleteReceipt({
        variables: { id: receipt.id },
      })
      setReceipts(receipts.filter((r) => r.id !== receipt.id))
    }
  }

  return (
    <TableRow>
      <TableCell padding='none'>
        <Typography variant="subtitle2" sx={{ ml: 2, fontWeight: 'bold' }}>
          Receipts
        </Typography>
      </TableCell>
      <TableCell padding="none" colSpan={2}>
        <List dense>
          {receipts.map((r: any) => (
            <ListItem
              key={r.id}
              secondaryAction={!paidByAmin && !readonly && (
                <Tooltip title="Delete receipt">
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteReceipt(r)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>)
              }
            >
              <ListItemButton disableGutters sx={{ width: 'auto' }} onClick={() => getFileDownloadLink({ variables: { id: r.id } })}>
                <ListItemIcon>
                  <CloudUploadIcon />
                </ListItemIcon>
                <ListItemText primary={r.path} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </TableCell>
      <TableCell align="center">
        {!paidByAmin && !readonly && (
          <Button
            component="label"
            role={undefined}
            variant="contained"
            color="secondary"
            size="small"
            tabIndex={-1}
            aria-label="upload receipt"
          >
            <VisuallyHiddenInput type="file" onChange={handleFileChange} />
            <CloudUploadIcon />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export default Receipts

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})
